import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Trans } from 'react-i18next';

const GDPRPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {t('gdpr.title', 'GDPR Compliance')}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            <Trans i18nKey="gdpr.subtitle">
              How <span className="font-brand">tvia.ai</span> protects your personal data and complies with the General Data Protection Regulation (GDPR).
            </Trans>
          </p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <div className="prose prose-lg max-w-none">
            {/* Introduction */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('gdpr.introduction.title', 'Our Commitment to Data Protection')}
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  <Trans i18nKey="gdpr.introduction.paragraph1">
                    At <span className="font-brand">tvia.ai</span>, we are committed to protecting your privacy and ensuring the security of your personal data. We fully comply with the General Data Protection Regulation (GDPR) and other applicable data protection laws.
                  </Trans>
                </p>
                <p>
                  {t('gdpr.introduction.paragraph2', 'This page explains how we collect, use, store, and protect your personal information, as well as your rights under GDPR. We believe in transparency and want you to understand exactly how your data is handled.')}
                </p>
              </div>
            </section>

            {/* What is GDPR */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('gdpr.whatIsGdpr.title', 'What is GDPR?')}
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  {t('gdpr.whatIsGdpr.paragraph1', 'The General Data Protection Regulation (GDPR) is a comprehensive data protection law that came into effect on May 25, 2018. It applies to all organizations operating within the EU and those that offer goods or services to individuals in the EU, regardless of where the organization is based.')}
                </p>
                <p>
                  {t('gdpr.whatIsGdpr.paragraph2', 'GDPR gives individuals greater control over their personal data and requires organizations to be more transparent about how they collect, use, and protect personal information.')}
                </p>
              </div>
            </section>

            {/* Data We Collect */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('gdpr.dataWeCollect.title', 'Personal Data We Collect')}
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  {t('gdpr.dataWeCollect.paragraph1', 'We collect and process the following types of personal data:')}
                </p>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <span className="text-cyan-600 mr-2">•</span>
                      <span>{t('gdpr.dataWeCollect.accountInfo', 'Account information (name, email address, password)')}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-cyan-600 mr-2">•</span>
                      <span>{t('gdpr.dataWeCollect.profileInfo', 'Profile information (contact details, preferences)')}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-cyan-600 mr-2">•</span>
                      <span>{t('gdpr.dataWeCollect.usageData', 'Usage data (how you interact with our platform)')}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-cyan-600 mr-2">•</span>
                      <span>{t('gdpr.dataWeCollect.legalData', 'Legal case information (when you file lawsuits or join class actions)')}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-cyan-600 mr-2">•</span>
                      <span>{t('gdpr.dataWeCollect.technicalData', 'Technical data (IP address, browser type, device information)')}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-cyan-600 mr-2">•</span>
                      <span>{t('gdpr.dataWeCollect.communicationData', 'Communication data (emails, support tickets)')}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* How We Use Data */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('gdpr.howWeUseData.title', 'How We Use Your Personal Data')}
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  {t('gdpr.howWeUseData.paragraph1', 'We use your personal data for the following purposes:')}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-blue-900 mb-2">
                      {t('gdpr.howWeUseData.service.title', 'Service Provision')}
                    </h4>
                    <p className="text-blue-800 text-sm">
                      {t('gdpr.howWeUseData.service.description', 'To provide our legal services, process lawsuits, and manage your account.')}
                    </p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-green-900 mb-2">
                      {t('gdpr.howWeUseData.communication.title', 'Communication')}
                    </h4>
                    <p className="text-green-800 text-sm">
                      {t('gdpr.howWeUseData.communication.description', 'To send you important updates, notifications, and respond to your inquiries.')}
                    </p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-purple-900 mb-2">
                      {t('gdpr.howWeUseData.improvement.title', 'Service Improvement')}
                    </h4>
                    <p className="text-purple-800 text-sm">
                      {t('gdpr.howWeUseData.improvement.description', 'To analyze usage patterns and improve our platform and services.')}
                    </p>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-orange-900 mb-2">
                      {t('gdpr.howWeUseData.compliance.title', 'Legal Compliance')}
                    </h4>
                    <p className="text-orange-800 text-sm">
                      {t('gdpr.howWeUseData.compliance.description', 'To comply with legal obligations and regulatory requirements.')}
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Legal Basis */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('gdpr.legalBasis.title', 'Legal Basis for Processing')}
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  {t('gdpr.legalBasis.paragraph1', 'Under GDPR, we process your personal data based on the following legal grounds:')}
                </p>
                <div className="space-y-3">
                  <div className="border-l-4 border-cyan-500 pl-4">
                    <h4 className="font-semibold text-gray-900">
                      {t('gdpr.legalBasis.consent.title', 'Consent')}
                    </h4>
                    <p className="text-sm text-gray-600">
                      {t('gdpr.legalBasis.consent.description', 'You have given clear consent for us to process your personal data for specific purposes.')}
                    </p>
                  </div>
                  <div className="border-l-4 border-blue-500 pl-4">
                    <h4 className="font-semibold text-gray-900">
                      {t('gdpr.legalBasis.contract.title', 'Contract Performance')}
                    </h4>
                    <p className="text-sm text-gray-600">
                      {t('gdpr.legalBasis.contract.description', 'Processing is necessary for the performance of our contract with you.')}
                    </p>
                  </div>
                  <div className="border-l-4 border-green-500 pl-4">
                    <h4 className="font-semibold text-gray-900">
                      {t('gdpr.legalBasis.legitimate.title', 'Legitimate Interests')}
                    </h4>
                    <p className="text-sm text-gray-600">
                      {t('gdpr.legalBasis.legitimate.description', 'Processing is necessary for our legitimate interests, provided these interests do not override your fundamental rights.')}
                    </p>
                  </div>
                  <div className="border-l-4 border-purple-500 pl-4">
                    <h4 className="font-semibold text-gray-900">
                      {t('gdpr.legalBasis.legal.title', 'Legal Obligation')}
                    </h4>
                    <p className="text-sm text-gray-600">
                      {t('gdpr.legalBasis.legal.description', 'Processing is necessary for compliance with our legal obligations.')}
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Your Rights */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('gdpr.yourRights.title', 'Your Rights Under GDPR')}
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  {t('gdpr.yourRights.paragraph1', 'GDPR gives you several important rights regarding your personal data:')}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">
                      {t('gdpr.yourRights.access.title', 'Right of Access')}
                    </h4>
                    <p className="text-gray-600 text-sm">
                      {t('gdpr.yourRights.access.description', 'You can request a copy of all personal data we hold about you.')}
                    </p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">
                      {t('gdpr.yourRights.rectification.title', 'Right of Rectification')}
                    </h4>
                    <p className="text-gray-600 text-sm">
                      {t('gdpr.yourRights.rectification.description', 'You can request correction of inaccurate or incomplete personal data.')}
                    </p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">
                      {t('gdpr.yourRights.erasure.title', 'Right of Erasure')}
                    </h4>
                    <p className="text-gray-600 text-sm">
                      {t('gdpr.yourRights.erasure.description', 'You can request deletion of your personal data in certain circumstances.')}
                    </p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">
                      {t('gdpr.yourRights.portability.title', 'Right of Portability')}
                    </h4>
                    <p className="text-gray-600 text-sm">
                      {t('gdpr.yourRights.portability.description', 'You can request transfer of your data to another service provider.')}
                    </p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">
                      {t('gdpr.yourRights.restriction.title', 'Right of Restriction')}
                    </h4>
                    <p className="text-gray-600 text-sm">
                      {t('gdpr.yourRights.restriction.description', 'You can request limitation of processing in certain circumstances.')}
                    </p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">
                      {t('gdpr.yourRights.objection.title', 'Right of Objection')}
                    </h4>
                    <p className="text-gray-600 text-sm">
                      {t('gdpr.yourRights.objection.description', 'You can object to processing based on legitimate interests.')}
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Data Security */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('gdpr.dataSecurity.title', 'Data Security Measures')}
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  {t('gdpr.dataSecurity.paragraph1', 'We implement appropriate technical and organizational measures to protect your personal data:')}
                </p>
                <div className="bg-green-50 p-6 rounded-lg">
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2">🔒</span>
                      <span>{t('gdpr.dataSecurity.encryption', 'End-to-end encryption for data in transit and at rest')}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2">🛡️</span>
                      <span>{t('gdpr.dataSecurity.access', 'Strict access controls and authentication measures')}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2">🔐</span>
                      <span>{t('gdpr.dataSecurity.backup', 'Regular security audits and data backups')}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2">👥</span>
                      <span>{t('gdpr.dataSecurity.training', 'Employee training on data protection and privacy')}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2">📋</span>
                      <span>{t('gdpr.dataSecurity.incident', 'Incident response procedures and breach notification protocols')}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Data Retention */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('gdpr.dataRetention.title', 'Data Retention')}
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  {t('gdpr.dataRetention.paragraph1', 'We retain your personal data only for as long as necessary to fulfill the purposes for which it was collected:')}
                </p>
                <div className="bg-yellow-50 p-6 rounded-lg">
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <span className="text-yellow-600 mr-2">•</span>
                      <span>{t('gdpr.dataRetention.account', 'Account data: Retained while your account is active and for a reasonable period after deactivation')}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-yellow-600 mr-2">•</span>
                      <span>{t('gdpr.dataRetention.legal', 'Legal case data: Retained as required by law and for legal proceedings')}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-yellow-600 mr-2">•</span>
                      <span>{t('gdpr.dataRetention.marketing', 'Marketing data: Retained until you withdraw consent or opt out')}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-yellow-600 mr-2">•</span>
                      <span>{t('gdpr.dataRetention.technical', 'Technical data: Retained for security and analytics purposes as needed')}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Contact Information */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('gdpr.contact.title', 'Exercising Your Rights')}
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  {t('gdpr.contact.paragraph1', 'To exercise any of your GDPR rights or if you have questions about our data practices, please contact us:')}
                </p>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">
                        {t('gdpr.contact.dataProtection', 'Data Protection Officer')}
                      </h4>
                      <p className="text-sm text-gray-600">dpo@tvia.ai</p>
                      <p className="text-sm text-gray-600">+1 (555) 123-4567</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">
                        {t('gdpr.contact.general', 'General Inquiries')}
                      </h4>
                      <p className="text-sm text-gray-600">privacy@tvia.ai</p>
                      <p className="text-sm text-gray-600">+1 (555) 123-4567</p>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <p className="text-sm text-gray-600">
                      {t('gdpr.contact.response', 'We will respond to your request within 30 days. If we need more time, we will notify you and explain why.')}
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Updates */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('gdpr.updates.title', 'Updates to This Policy')}
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  {t('gdpr.updates.paragraph1', 'We may update this GDPR compliance page from time to time to reflect changes in our practices or applicable laws. We will notify you of any material changes by posting the updated policy on our website and updating the "Last Updated" date.')}
                </p>
                <p>
                  {t('gdpr.updates.paragraph2', 'We encourage you to review this page periodically to stay informed about how we protect your personal data.')}
                </p>
              </div>
            </section>
          </div>
        </div>

        {/* Related Links */}
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            {t('gdpr.relatedLinks', 'Related Information')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link
              to="/privacy"
              className="bg-gray-50 rounded-lg p-6 hover:bg-gray-100 transition-colors duration-200"
            >
              <div className="text-3xl mb-4">🔒</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t('gdpr.privacyPolicy', 'Privacy Policy')}
              </h3>
              <p className="text-gray-600 text-sm">
                {t('gdpr.privacyDescription', 'Detailed information about our data practices')}
              </p>
            </Link>
            <Link
              to="/cookies"
              className="bg-gray-50 rounded-lg p-6 hover:bg-gray-100 transition-colors duration-200"
            >
              <div className="text-3xl mb-4">🍪</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t('gdpr.cookiePolicy', 'Cookie Policy')}
              </h3>
              <p className="text-gray-600 text-sm">
                {t('gdpr.cookieDescription', 'How we use cookies and tracking technologies')}
              </p>
            </Link>
            <Link
              to="/contact"
              className="bg-gray-50 rounded-lg p-6 hover:bg-gray-100 transition-colors duration-200"
            >
              <div className="text-3xl mb-4">📞</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t('gdpr.contactUs', 'Contact Us')}
              </h3>
              <p className="text-gray-600 text-sm">
                {t('gdpr.contactDescription', 'Get in touch with our privacy team')}
              </p>
            </Link>
          </div>
        </div>

        {/* Footer Note */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>
            {t('gdpr.lastUpdated', 'Last updated:')} {new Date().toLocaleDateString()}
          </p>
          <p className="mt-2">
            {t('gdpr.version', 'Version:')} 1.0
          </p>
        </div>
      </div>
    </div>
  );
};

export default GDPRPage;